import Component from '@glimmer/component'
import { reportingTitle, reportingToolTip as toolTipConfig } from 'teacher-ui-client/utils/giraffe/highcharts-config'
import { range, round } from 'lodash'
import { colors } from '@colors'
import { BaseHighcharterComponent } from 'teacher-ui-client/components/base/highcharter'
import styleBgColor from 'teacher-ui-client/helpers/style-bg-color'
import type * as Highcharts from 'highcharts'

interface SeriesBarOptions {
  data: Array<Highcharts.PointOptionsObject>
  name: string
}

interface Args {
  categories?: string[]
  customLegend?: { name: string; color: string }[]
  data: SeriesBarOptions[]
  plotLines?: Highcharts.YAxisPlotLinesOptions[]
  tickPositions?: Highcharts.AxisTickPositionsArray
  title?: string
  toolTipFormatter: Highcharts.TooltipFormatterCallbackFunction
  yAxis?: Highcharts.YAxisOptions
  yAxisLabel?: string
  yAxisLabelFormatter: (any: Highcharts.AxisLabelsFormatterContextObject) => string
  yAxisOffset?: number
  yRange?: [number, number]
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

const disabled = { enabled: false }

export class SegmentedBar extends Component<Signature> {
  chartSpacing = [10, 10, 0, 10]

  plotBackgroundColor = 'white'

  xAxisGridLineColor = colors.dustyBlack100

  get chartOptions(): Highcharts.Options {
    return {
      colors: [],
      chart: {
        type: 'bar',
        plotBackgroundColor: this.plotBackgroundColor,
        backgroundColor: this.plotBackgroundColor,
        spacing: this.chartSpacing,
        marginTop: 80,
      },
      credits: disabled,
      title: undefined,
      yAxis: this.yAxis,
      xAxis: this.xAxis,
      plotOptions: this.plotOptions,
      tooltip: this.toolTip,
      legend: disabled,
    }
  }

  get content(): Highcharts.SeriesBarOptions[] {
    return this.args.data.reverse().map((item) => {
      const dataItems = item.data?.map((dataItem) => {
        return {
          ...dataItem,
          y: dataItem.y ?? dataItem.value,
        }
      })

      return {
        name: item.name,
        data: dataItems,
        type: 'bar',
      }
    })
  }

  get gridLineColor() {
    return this.xAxisGridLineColor
  }

  get highchartOptions(): Highcharts.Options {
    return { ...this.chartOptions, series: this.content }
  }

  get plotLines() {
    return this.args.plotLines ?? []
  }

  get plotOptions(): Highcharts.PlotOptions {
    const states = this.plotStates

    return {
      bar: {
        borderWidth: 1,
        borderColor: this.plotBackgroundColor,
        stacking: 'normal',
        groupPadding: 0.02,
        pointPadding: 0.08,
        colorByPoint: true,
        states,
      },
    }
  }

  get plotStates() {
    return {
      hover: {
        animation: false,
      },
      inactive: {
        animation: false,
        opacity: 1,
      },
      normal: {
        animation: false,
      },
      select: {
        animation: false,
      },
    }
  }

  get tickPositions(): Highcharts.AxisTickPositionsArray {
    if (this.args.tickPositions) return this.args.tickPositions

    const tickAmount = 10
    const [yRangeMin, yRangeMax] = this.args.yRange ?? []

    const min = yRangeMin || 0
    const max = yRangeMax || (this.args.data?.length ?? 0) + min

    const unroundedStepSize = (max - min) / tickAmount
    let precision = 0
    if (unroundedStepSize >= 10) {
      precision = -1 // -1 rounds it upto the nearest 10
    }
    const stepSize = round(unroundedStepSize, precision)
    const tickPositions = range(min, max, stepSize)
    const expectedLastTick = max
    const lastTick = tickPositions[tickPositions.length - 1]
    if (lastTick !== expectedLastTick) {
      tickPositions.push(expectedLastTick)
    }
    // Add an extra one at the end so that the last datapoint is displayed
    // on the chart
    tickPositions.push(tickPositions[tickPositions.length - 1] + 1)
    return tickPositions
  }

  get toolTip(): Highcharts.TooltipOptions {
    return toolTipConfig('y', '', {
      formatter: this.args.toolTipFormatter,
      backgroundColor: colors.neutral400,
    })
  }

  get xAxis(): Highcharts.XAxisOptions {
    const allowDecimals = false

    return {
      title: reportingTitle(''),
      categories: this.args.categories ?? [],
      gridLineWidth: 1,
      gridLineColor: this.gridLineColor,
      minPadding: 0,
      maxPadding: 0,
      tickWidth: 0,
      allowDecimals,
      labels: { enabled: false },
    }
  }
  get yAxis(): Highcharts.YAxisOptions {
    if (this.args.yAxis) return this.args.yAxis

    const [min, max] = this.args.yRange ?? [0, 0]

    const allowDecimals = false

    const yAxisLabelFormatter = this.args.yAxisLabelFormatter

    return {
      title: reportingTitle(this.yAxisLabel),
      visible: true,
      min,
      max: max + 2,
      plotLines: this.plotLines,
      plotBands: [],
      allowDecimals,
      gridLineWidth: 0,
      offset: this.yAxisOffset,
      opposite: true,
      showFirstLabel: true,
      showLastLabel: true,
      tickPositions: this.tickPositions,
      startOnTick: false,
      labels: {
        formatter(...args) {
          // We know the last is our special hidden tick to bring the last bar
          // onto the chart data space, so don't show it, and don't pass it to
          // the formatter, it's an internal thing only.
          return this.isLast ? '' : yAxisLabelFormatter.apply(this, args)
        },
      },
    }
  }

  get yAxisLabel() {
    return this.args.yAxisLabel ?? ''
  }

  get yAxisOffset() {
    return this.args.yAxisOffset ?? 0
  }

  <template>
    <div class="h-full" ...attributes>
      {{#if @customLegend}}
        <div data-test-chart-html-header class="absolute right-0 left-0 hidden lg:block">
          {{#if @title}}
            <div data-test-chart-html-title class="relative z-3 hidden text-center font-bold xl:block">
              {{@title}}
            </div>
          {{/if}}
          <div data-test-chart-html-legend class="relative z-3 ml-3 flex h-6 flex-nowrap items-center overflow-hidden">
            {{#each @customLegend as |legendItem index|}}
              <div data-test-chart-html-legend-item={{index}} class="mr-3 overflow-hidden whitespace-nowrap">
                <div
                  data-test-chart-html-legend-circle
                  class="inline-block h-[5px] w-[5px] rounded-full xl:h-[10px] xl:w-[10px]"
                  style={{styleBgColor legendItem.color}}
                >
                </div>
                <div class="inline-block px-1 py-0 text-xs xl:text-sm">{{legendItem.name}}</div>
              </div>
            {{/each}}
          </div>
        </div>
      {{/if}}
      <BaseHighcharterComponent class="h-full" @options={{this.highchartOptions}} />
    </div>
  </template>
}

export default SegmentedBar
